import { datadogRum } from '@datadog/browser-rum'
import debug from '../utils/console'
import { isDebugEnabled } from '../utils/debug/isDebugEnabled'
import { startConsentTimingVitals } from '../utils/consent/consentTimingVitals'

const logger = debug.withTag('consent-timing-vitals')

const withoutTrailingSlash = (path: string) => path.replace(/\/+$/, '') || '/'

const isPrerendering = () => {
  const doc: Document & { prerendering?: boolean } = document
  return doc.prerendering === true
}

/**
 * Sends the consent bootstrap timings and the consent decision to RUM. See
 * utils/consent/consentTimingVitals.ts.
 *
 * RUM starts later, from the tenant's app.vue, and the SDK holds these calls
 * until it does. Nothing is sent when the tenant has no RUM configured, and a
 * page that loaded in a background tab reports nothing, since its timings
 * would be about the visitor and not about the page.
 */
export default defineNuxtPlugin({
  name: 'consent-timing-vitals',
  setup() {
    const config = useRuntimeConfig()
    if (
      !config.public.datadogRumApplicationId ||
      !config.public.datadogRumClientToken
    ) {
      return
    }
    if (document.visibilityState === 'hidden' && !isPrerendering()) return

    const debugEnabled = isDebugEnabled()
    // Only a measurement, so a failure here must never take the page down.
    try {
      const vitals = startConsentTimingVitals({
        addDurationVital: (name, options) => {
          if (debugEnabled) logger.info('Consent timing', name, options)
          datadogRum.addDurationVital(name, options)
        },
        setViewContextProperty: (key, value) => {
          if (debugEnabled) logger.info('Consent decision', key, value)
          datadogRum.setViewContextProperty(key, value)
        },
      })

      // Another path is another page: its ad request must not settle the
      // landing page's timings or tag its view. A query change (a category
      // tab) is the same page, as in first-ad-visible.
      const removeRouteHook = useRouter().afterEach((to, from) => {
        if (
          from.matched.length > 0 &&
          withoutTrailingSlash(to.path) !== withoutTrailingSlash(from.path)
        ) {
          vitals.endLandingPage()
          removeRouteHook()
        }
      })
    } catch (error) {
      logger.warn('Could not start sending consent timings', error)
    }
  },
})
